'use client';

import { Check } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
// import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	// Tooltip,
	// TooltipContent,
	TooltipProvider,
	// TooltipTrigger,
} from '@/components/ui/tooltip';
// import { serviceTypes } from '@/lib/constants/pricing-data';
import { components } from '@/types/strapi';

// interface ServiceType extends PricingComparision {
// 	product_id: string;
// 	label: string;
// 	yearly_discount: string;
// 	plans: components['schemas']['ItemsPlanItemComponent'][];
// }

type PricingComparision =
	components['schemas']['DynamicZoneNewPricingComponent'];

interface Pricing_ extends PricingComparision {
	service_types: components['schemas']['Product'][];
	isFirst: boolean;
	// service_types: components['schemas'][]
}
// interface Pricing {
// 	heading: string;
// 	sub_heading: string;
// 	service_types: ServiceType[];
// }

export function PricingComparison({
	heading,
	sub_heading,
	service_types,
	isFirst,
}: Pricing_) {
	// console.log(service_types);
	const [activeService, setActiveService] = useState(
		service_types?.[0]?.product_id || '',
	);
	// console.log(service_types, 'service_types');
	useEffect(() => {
		if (!activeService && service_types?.[0]?.product_id) {
			setActiveService(service_types[0].product_id);
		}
	}, [activeService]);
	return (
		<TooltipProvider>
			<section
				id="pricing"
				className="container mx-auto bg-gradient-to-b from-gray-50 to-white px-4 py-8">
				<div className="mx-auto mb-4 max-w-3xl text-center">
					{isFirst ? (
						<h1 className="mb-3 text-4xl font-bold tracking-tight md:text-5xl">
							{/* Simple, Transparent Pricing */}
							{heading}
						</h1>
					) : (
						<h2 className="mb-3 text-4xl font-bold tracking-tight md:text-5xl">
							{/* Simple, Transparent Pricing */}
							{heading}
						</h2>
					)}

					<p className="text-xl text-gray-600/90">
						{/* Choose the perfect plan for your communication needs */}
						{sub_heading}
					</p>
				</div>
				<Tabs
					defaultValue={
						service_types?.[0]?.product_id || 'transactional'
					}
					className="w-full"
					onValueChange={setActiveService}>
					<TabsList className="container mx-auto flex h-auto flex-wrap justify-center border-0 bg-transparent">
						{service_types?.map(service => (
							<TabsTrigger
								id={
									service.label
										?.replace(/\s+/g, '-')
										?.toLowerCase() ?? 'default'
								}
								key={service.product_id}
								value={service.product_id || 'default'}
								className={`relative px-3 text-base font-medium transition-all focus:outline-none data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-600 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:scale-105 data-[state=inactive]:hover:text-gray-900`}>
								{service.label}
							</TabsTrigger>
						))}
					</TabsList>
					{service_types?.map(service => (
						<TabsContent
							key={service.product_id}
							value={service.product_id || ''}
							className="mx-auto mt-0 grid max-w-7xl gap-8 lg:grid-cols-3">
							{service?.plans?.map(
								(
									plan: components['schemas']['ItemsPlanItemComponent'],
								) => (
									<Card
										key={plan.name}
										className={`relative h-full border transition-all duration-300 hover:border-blue-500 ${
											plan.popular
												? 'border-blue-200 shadow-xl'
												: 'border-gray-100 shadow-lg'
										} group hover:scale-[1.02] hover:shadow-2xl`}>
										{/* {plan.popular && (
											<Badge className="absolute -top-4 left-1/2 flex w-[200px] -translate-x-1/2 transform justify-center bg-blue-700 px-4 py-1 text-base font-semibold text-white hover:bg-blue-800">
												Most Popular
											</Badge>
										)} */}

										<CardHeader className="pb-8 pt-8">
											<div
												className={`mb-6 flex h-14 w-14 items-center justify-center`}>
												{plan?.icon?.url && (
													<Image
														src={plan.icon.url}
														alt={
															plan.icon
																.alternativeText ||
															'Alternate Text'
														}
														width={28}
														height={28}
														className="rounded-full"
													/>
												)}
											</div>
											<h3 className="mb-2 text-2xl font-bold">
												{plan.name}
											</h3>
											{plan.description && (
												<p className="text-gray-600/90">
													{plan.description}
												</p>
											)}
										</CardHeader>

										<CardContent className="mb-8 pb-8">
											<div className="flex items-baseline gap-2">
												{/* {!plan.monthly_price &&
													!plan.yearly_price && (
														<span className="text-3xl font-bold text-gray-800">
															Custom Pricing
														</span>
													)} */}
												{plan.price && (
													<div className="flex flex-wrap items-baseline gap-2">
														<span className="text-5xl font-bold">
															{plan.price}
														</span>
														{plan.price_per_text && (
															<span className="text-gray-600/90">
																{
																	plan.price_per_text
																}
															</span>
														)}
													</div>
												)}
											</div>
											<div className="mt-3 flex items-center justify-between text-sm text-gray-600/90">
												{plan.price_description && (
													<span className="text-sm text-gray-600/90">
														{plan.price_description}
													</span>
												)}
											</div>
											<div className="space-y-6">
												{plan.message_pricing &&
													plan.message_pricing
														.length > 0 && (
														<div className="rounded-lg bg-white/50 p-4">
															<h4 className="mb-2 font-semibold">
																Message Types &
																Pricing
															</h4>
															{plan.message_pricing?.map(
																(item, ind) => (
																	<div
																		key={
																			ind
																		}
																		className="flex justify-between py-1 text-sm">
																		{item.key && (
																			<span className="text-gray-600">
																				{
																					item.key
																				}
																			</span>
																		)}
																		{item.value && (
																			<span className="font-medium">
																				{
																					item.value
																				}
																			</span>
																		)}
																	</div>
																),
															)}
														</div>
													)}

												{/* Other Charges - Only show for RCS */}
												{plan.other_charges &&
													plan.other_charges.length >
														0 && (
														<div className="rounded-lg bg-white/50 p-4">
															<h4 className="mb-2 font-semibold">
																Other Charges
															</h4>
															{plan.other_charges.map(
																(item, ind) => (
																	<div
																		key={
																			ind
																		}
																		className="flex justify-between py-1 text-sm">
																		{item.key && (
																			<span className="text-gray-600">
																				{
																					item.key
																				}
																			</span>
																		)}
																		{item.value && (
																			<span className="font-medium">
																				{
																					item.value
																				}
																			</span>
																		)}
																	</div>
																),
															)}
														</div>
													)}

												{/* Features - Show for all services */}
												{plan.features_list &&
													plan.features_list.length >
														0 && (
														<div className="space-y-3">
															<h4 className="font-semibold">
																Features
															</h4>
															{plan.features_list?.map(
																(
																	feature,
																	featureIndex,
																) => (
																	<div
																		key={
																			featureIndex
																		}
																		className="flex items-start gap-3">
																		<Check className="h-5 w-5 flex-shrink-0 text-green-500" />
																		<span className="text-gray-600/90">
																			{
																				feature.text
																			}
																		</span>
																	</div>
																),
															)}
														</div>
													)}
											</div>
										</CardContent>

										<CardFooter className="pb-8">
											<Link
												href="/contact-us"
												className="inline-flex h-12 w-full items-center justify-center rounded-md border border-blue-600 bg-blue-600 px-6 text-base font-medium text-white transition-all duration-300 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
												Get Started
											</Link>
										</CardFooter>
									</Card>
								),
							)}
						</TabsContent>
					))}
				</Tabs>
				<div className="mt-6 text-center text-gray-600/90">
					<p className="mx-auto max-w-2xl">
						All plans include access to our API, documentation, and
						basic support.
						<br />
						Need a custom plan?{' '}
						<Link
							href="/contact-us"
							className="mt-1 inline-block rounded text-sm font-medium text-blue-500 underline underline-offset-4 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400">
							Contact our sales team
						</Link>{' '}
						for volume-based pricing.
					</p>
				</div>
			</section>
		</TooltipProvider>
	);
}
